import { useCallback, useState } from 'react';
import { useAccount, usePublicClient, useSwitchChain, useWriteContract } from 'wagmi';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { BSC_CHAIN_ID } from '../../lib/addresses';
import { bscTx, shortAddr } from '../../lib/format';
import { IS_DEPLOYED } from '../../lib/contract';

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

// Унифицирует lifecycle подписи tx:
// idle → signing (подпись в кошельке) → confirming (ждём receipt) → success | error
// В mock-режиме (IS_DEPLOYED=false) — mockAction() выполняется локально с имитацией задержек.
export function useTxLifecycle({ invalidateKeys = [] } = {}) {
  const publicClient = usePublicClient();
  const { chainId } = useAccount();
  const { switchChainAsync } = useSwitchChain();
  const { writeContractAsync } = useWriteContract();
  const queryClient = useQueryClient();

  const [status, setStatus] = useState('idle');
  const [hash, setHash] = useState(null);
  const [error, setError] = useState(null);

  const reset = useCallback(() => {
    setStatus('idle');
    setHash(null);
    setError(null);
  }, []);

  const send = useCallback(
    async ({ abi, address, functionName, args = [], label = 'Transaction', successMessage, mockAction }) => {
      setError(null);
      setHash(null);

      // ---- MOCK PATH ----
      if (!IS_DEPLOYED) {
        const toastId = toast.loading(`${label} — demo sign`);
        setStatus('signing');
        await sleep(500);
        setStatus('confirming');
        toast.loading(`${label} — confirming…`, { id: toastId });
        await sleep(900);
        try {
          mockAction?.();
          setStatus('success');
          toast.success(successMessage || `${label} confirmed (demo)`, { id: toastId });
          for (const key of invalidateKeys) {
            queryClient.invalidateQueries({ queryKey: key });
          }
          return { success: true, hash: '0xmock', receipt: null };
        } catch (e) {
          setStatus('error');
          setError(e);
          toast.error(`${label} failed`, { id: toastId, description: e?.message || 'Unknown error' });
          return { success: false, error: e };
        }
      }

      // ---- REAL PATH ----
      try {
        if (chainId && chainId !== BSC_CHAIN_ID) {
          toast.info('Switching network to BSC…');
          await switchChainAsync({ chainId: BSC_CHAIN_ID });
        }

        setStatus('signing');
        const toastId = toast.loading(`${label} — sign in wallet`);

        let txHash;
        try {
          txHash = await writeContractAsync({ abi, address, functionName, args });
        } catch (e) {
          toast.dismiss(toastId);
          setStatus('error');
          setError(e);
          const msg = parseError(e);
          if (msg === 'UserRejected') toast.error('Transaction cancelled');
          else toast.error(`${label} failed`, { description: msg });
          return { success: false, error: e };
        }

        setHash(txHash);
        setStatus('confirming');
        toast.loading(`${label} — confirming…`, {
          id: toastId,
          description: shortAddr(txHash, 10, 8),
          action: {
            label: 'BscScan',
            onClick: () => window.open(bscTx(txHash), '_blank', 'noopener,noreferrer'),
          },
        });

        const receipt = await publicClient.waitForTransactionReceipt({ hash: txHash });

        if (receipt.status === 'success') {
          setStatus('success');
          toast.success(successMessage || `${label} confirmed`, {
            id: toastId,
            description: shortAddr(txHash, 10, 8),
            action: {
              label: 'BscScan',
              onClick: () => window.open(bscTx(txHash), '_blank', 'noopener,noreferrer'),
            },
          });
          for (const key of invalidateKeys) {
            queryClient.invalidateQueries({ queryKey: key });
          }
          return { success: true, hash: txHash, receipt };
        } else {
          setStatus('error');
          toast.error(`${label} reverted on-chain`, {
            id: toastId,
            action: {
              label: 'BscScan',
              onClick: () => window.open(bscTx(txHash), '_blank', 'noopener,noreferrer'),
            },
          });
          return { success: false, hash: txHash, receipt };
        }
      } catch (e) {
        setStatus('error');
        setError(e);
        toast.error(`${label} failed`, { description: parseError(e) });
        return { success: false, error: e };
      }
    },
    [chainId, switchChainAsync, writeContractAsync, publicClient, queryClient, invalidateKeys],
  );

  return { send, status, hash, error, reset };
}

function parseError(e) {
  if (!e) return 'Unknown error';
  if (e.name === 'UserRejectedRequestError' || /User rejected|User denied/i.test(e.message ?? '')) {
    return 'UserRejected';
  }
  const rev = e.shortMessage || e.details || e.metaMessages?.[0] || e.message || 'Unknown error';
  return String(rev).slice(0, 180);
}
